import React from "react";
import { Switch, cn, extendVariants } from "@heroui/react";
import getDisabledStyle from "./helpers/disabledState";

export const CustomSwitch = extendVariants(Switch, getDisabledStyle("wrapper"));

interface HeroSwitchProps {
  children?: React.ReactNode;
  value?: string;
  name?: string;
  size?: "sm" | "md" | "lg";
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  thumbIcon?: React.ReactNode;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  isSelected?: boolean;
  defaultSelected?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  disableAnimation?: boolean;
  classNames?: { [key: string]: string };
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: (isSelected: boolean) => void;
  isMiddle?: boolean;
}

/**
 * HeroSwitch component is a customizable switch component.
 *
 * @param {HeroSwitchProps} props - The properties for the HeroSwitch component.
 * @returns {JSX.Element} The rendered HeroSwitch component.
 */
const HeroSwitch: React.FC<HeroSwitchProps> = ({
  children,
  value,
  name,
  size = "md",
  color = "primary",
  thumbIcon,
  startContent,
  endContent,
  isSelected,
  defaultSelected,
  isReadOnly,
  isDisabled = false,
  disableAnimation = false,
  classNames = {},
  className,
  onChange,
  onValueChange,
  isMiddle,
}) => {
  return (
    <CustomSwitch
      value={value}
      name={name}
      size={size}
      color={color}
      thumbIcon={thumbIcon}
      startContent={startContent}
      endContent={endContent}
      isSelected={isSelected}
      defaultSelected={defaultSelected}
      isReadOnly={isReadOnly}
      isDisabled={isDisabled}
      disableAnimation={disableAnimation}
      classNames={{
        ...classNames,
        thumb: cn(
          classNames.thumb,
          isMiddle && "group-data-[selected=true]:translate-x-[-10px]",
        ),
      }}
      className={className}
      onChange={onChange}
      onValueChange={onValueChange}
    >
      {children}
    </CustomSwitch>
  );
};

export default HeroSwitch;
